import type { FieldConfig, GeoGridTypeJson, ProjectTypeJson } from './ValidationService';

/**
 * Single normalizer of the FE↔BE payload values ([CONTRACT] run-text-fields v2.1).
 * Types come from the project schema (group + field → type) plus the explicit tables below;
 * keys known to none of them pass through untouched.
 */

export type WirePolicy = 'preserve' | 'strict';
export type WireDirection = 'inbound' | 'outbound';
export type WireValueErrorReason = 'malformed-number' | 'not-an-integer' | 'out-of-int32-range' | 'not-a-string';

export class WireValueError extends Error {
    readonly fieldPath: string;
    readonly reason: WireValueErrorReason;

    constructor(fieldPath: string, reason: WireValueErrorReason) {
        super(`Invalid value for ${fieldPath} (${reason})`);
        this.name = 'WireValueError';
        this.fieldPath = fieldPath;
        this.reason = reason;
        // The ES5 target breaks instanceof on Error subclasses without this.
        Object.setPrototypeOf(this, WireValueError.prototype);
    }
}

export interface WireSchemaContext {
    projectType: string;
    method: string;
    direction: WireDirection;
    schema?: ProjectTypeJson;
    geoGridSchema: GeoGridTypeJson;
}

/** C1: free-text metadata at the root of projectInfo.data. */
const TEXT_METADATA_FIELDS = ['designerFirmCity', 'notes', 'nrProject'];

/** C4: counters the backend reads with GetInt32 (designLifePassenger is not read, so not listed). */
const INTEGER_FIELDS: Record<string, Record<string, string[]>> = {
    unpaved: { load: ['numPassages'] },
    railways: {
        trafficData: [
            'numberOfTrainsPerDay',
            'numberOfCarsPerTrain',
            'numberOfAxisPerCar',
            'designLife',
            'numberOfTrainsPerDayCargo',
            'numberOfCarsPerTrainCargo',
            'numberOfAxisPerCarCargo',
            'designLifeCargo',
            'numberOfTrainsPerDayPassenger',
            'numberOfCarsPerTrainPassenger',
            'numberOfAxisPerCarPassenger',
        ],
    },
};

/** C5: wire type differs from the UI schema type. Applied outbound only; inbound keeps the UI value. */
const OUTBOUND_OVERRIDES: Record<string, Record<string, 'number'>> = {
    geogridItem: { designStrain: 'number' },
};

const GEOGRID_KEY = 'geoGrid';
const GEOGRID_ITEMS_KEY = 'geogrids';
const GEOGRID_ITEM_GROUP = 'geogridItem';
const INT32_MIN = -2147483648;
const INT32_MAX = 2147483647;
const DECIMAL_GRAMMAR = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;

type LeafClass = 'text' | 'number' | 'integer';
type Fields = Map<string, LeafClass>;

const isPlainObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

/** C3 grammar: the whole trimmed string must be a finite decimal (exponent allowed). */
export const parseWireDecimal = (raw: string): number | null => {
    const trimmed = raw.trim();
    if (!DECIMAL_GRAMMAR.test(trimmed)) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
};

export const isWireIntegerField = (projectType: string, group: string, field: string): boolean => INTEGER_FIELDS[projectType]?.[group]?.includes(field) ?? false;

/** C4 counter rule shared with the inline form validation: absent is fine, otherwise a whole Int32. */
export const isWireInt32 = (value: unknown): boolean => value === undefined || value === null || (typeof value === 'number' && Number.isInteger(value) && value >= INT32_MIN && value <= INT32_MAX);

/** Preserving C3 rule: a valid numeric string becomes its number, anything else is returned unchanged. */
export const normalizeNumberFieldValue = (value: unknown): unknown => {
    if (typeof value !== 'string') return value;
    const parsed = parseWireDecimal(value);
    return parsed === null ? value : parsed;
};

const isEmptySentinel = (value: unknown): boolean => value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

const normalizeText = (value: unknown, path: string, policy: WirePolicy, booleansAllowed: boolean): unknown => {
    if (value === null || value === undefined || typeof value === 'string') return value;
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    if (typeof value === 'boolean' && booleansAllowed) return value;
    if (policy === 'strict') throw new WireValueError(path, 'not-a-string');
    return value;
};

const normalizeNumber = (value: unknown, path: string, policy: WirePolicy, integer: boolean): unknown => {
    if (isEmptySentinel(value)) return value;
    const parsed = typeof value === 'number' ? (Number.isFinite(value) ? value : null) : typeof value === 'string' ? parseWireDecimal(value) : null;
    if (parsed === null) {
        if (policy === 'strict') throw new WireValueError(path, 'malformed-number');
        return value;
    }
    if (integer && policy === 'strict' && !isWireInt32(parsed)) {
        throw new WireValueError(path, Number.isInteger(parsed) ? 'out-of-int32-range' : 'not-an-integer');
    }
    return parsed;
};

const normalizeLeaf = (value: unknown, leafClass: LeafClass, path: string, policy: WirePolicy): unknown =>
    leafClass === 'text' ? normalizeText(value, path, policy, false) : normalizeNumber(value, path, policy, leafClass === 'integer');

const fieldsOf = (configs: FieldConfig[] | undefined, projectType: string, group: string): Fields =>
    new Map((configs ?? []).map(field => [field.name, field.type !== 'number' ? 'text' : isWireIntegerField(projectType, group, field.name) ? 'integer' : 'number'] as const));

const normalizeGroup = (groupData: Record<string, unknown>, fields: Fields, path: string, policy: WirePolicy): Record<string, unknown> => {
    const result: Record<string, unknown> = { ...groupData };
    Object.keys(groupData).forEach(key => {
        const leafClass = fields.get(key);
        if (leafClass) result[key] = normalizeLeaf(groupData[key], leafClass, `${path}.${key}`, policy);
    });
    return result;
};

const normalizeGeogridItem = (item: unknown, path: string, ctx: WireSchemaContext, policy: WirePolicy): unknown => {
    if (!isPlainObject(item)) return item;
    const fields = fieldsOf(ctx.geoGridSchema.fields[GEOGRID_ITEM_GROUP], ctx.projectType, GEOGRID_ITEM_GROUP);
    Object.keys(OUTBOUND_OVERRIDES[GEOGRID_ITEM_GROUP]).forEach(name => {
        if (ctx.direction === 'outbound') fields.set(name, OUTBOUND_OVERRIDES[GEOGRID_ITEM_GROUP][name]);
        else fields.delete(name);
    });
    return normalizeGroup(item, fields, path, policy);
};

/**
 * geoGrid object: leaves of the project schema's own geoGrid group (Unpaved, Railway) sit at its first
 * level; nested objects are groups of the geogrid schema, whose geogrids[] items use geogridItem.
 */
const normalizeGeoGrid = (geoGrid: Record<string, unknown>, ctx: WireSchemaContext, policy: WirePolicy): Record<string, unknown> => {
    const leaves = fieldsOf(ctx.schema?.fields[GEOGRID_KEY], ctx.projectType, GEOGRID_KEY);
    const result: Record<string, unknown> = { ...geoGrid };
    Object.keys(geoGrid).forEach(key => {
        const value = geoGrid[key];
        const path = `${GEOGRID_KEY}.${key}`;
        const groupConfigs = key === GEOGRID_ITEM_GROUP ? undefined : ctx.geoGridSchema.fields[key];
        if (isPlainObject(value) && groupConfigs) {
            const group = normalizeGroup(value, fieldsOf(groupConfigs, ctx.projectType, key), path, policy);
            const items = value[GEOGRID_ITEMS_KEY];
            if (Array.isArray(items)) group[GEOGRID_ITEMS_KEY] = items.map((item, index) => normalizeGeogridItem(item, `${path}.${GEOGRID_ITEMS_KEY}[${index}]`, ctx, policy));
            result[key] = group;
            return;
        }
        const leafClass = leaves.get(key);
        if (leafClass) result[key] = normalizeLeaf(value, leafClass, path, policy);
    });
    return result;
};

const normalizeContainer = (container: Record<string, unknown>, ctx: WireSchemaContext, policy: WirePolicy): Record<string, unknown> => {
    const result: Record<string, unknown> = { ...container };
    Object.keys(container).forEach(key => {
        const value = container[key];
        if (!isPlainObject(value)) return;
        if (key === GEOGRID_KEY) result[key] = normalizeGeoGrid(value, ctx, policy);
        else if (ctx.schema?.fields[key]) result[key] = normalizeGroup(value, fieldsOf(ctx.schema.fields[key], ctx.projectType, key), key, policy);
    });
    return result;
};

/**
 * Accepts FLAT (Redux) and NESTED (`data[method]`) shapes; metadata always at the root. Pure and idempotent:
 * returns a new object and never mutates the input. `strict` throws WireValueError on the first invalid value.
 */
export const normalizeProjectData = (data: Record<string, unknown>, ctx: WireSchemaContext, policy: WirePolicy): Record<string, unknown> => {
    const isNested = !!ctx.method && Object.prototype.hasOwnProperty.call(data, ctx.method) && isPlainObject(data[ctx.method]);
    const result = isNested ? { ...data } : normalizeContainer(data, ctx, policy);
    if (isNested) {
        result[ctx.method] = normalizeContainer(data[ctx.method] as Record<string, unknown>, ctx, policy);
        const rootGeoGrid = data[GEOGRID_KEY];
        if (isPlainObject(rootGeoGrid)) result[GEOGRID_KEY] = normalizeGeoGrid(rootGeoGrid, ctx, policy);
    }
    TEXT_METADATA_FIELDS.forEach(field => {
        if (Object.prototype.hasOwnProperty.call(data, field)) result[field] = normalizeText(data[field], field, policy, true);
    });
    return result;
};
